import React from 'react';
import { toast } from 'react-toastify';
import client from 'shared/client';
import { useRecoilValue } from 'recoil';
import { useHistory } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';

import { ToastBody } from 'components';
import {
  STORAGE_KEY,
  QLIK_ERROR_PATTERNS,
  DATADOG_LOG_EVENT
} from 'shared/enums';
import { programFilterAtom } from '../store/program-filter';
import useMutationObserver from './useMutationObserver';
import { hasAdminRole } from 'shared/helpers/hasAdminRole';
import { isInternalEmail } from 'shared/helpers/isInternalEmail';

/**
 * @description
 * This hook is used to detect when qlik connection is disturbed.
 * When that happens it will toast an error message and log to datadog
 *
 */
const useQlikConnectionObserver = ({ user }) => {
  const history = useHistory();
  const programFilter = useRecoilValue(programFilterAtom);

  const mutationCallback = async () => {
    const qlikModalBodyContent = document.body.querySelector(
      'div.lui-dialog__body'
    )?.textContent;

    const isAdmin = hasAdminRole(user);
    const isInternal = isInternalEmail(user?.emails); // To signal if an employee triggered the error with a non-admin test account.

    const ip = localStorage.getItem(STORAGE_KEY.IP_ADDR);
    const userId = user?._id || client.cache.data.data?.ROOT_QUERY?.user?._id;
    const userUUID =
      user?.uuid || client.cache.data.data?.ROOT_QUERY?.user?.uuid;

    const logBody = {
      ip,
      userId: userId,
      userUUID: userUUID,
      browser: navigator.userAgent,
      url: window.location.href,
      error: qlikModalBodyContent,
      schoolId: programFilter?.schoolId,
      isAdmin,
      isInternal
    };

    if (qlikModalBodyContent) {
      document.querySelectorAll(`[qs-angular-overlay]`)[0]?.remove();
    }

    if (qlikModalBodyContent?.includes(QLIK_ERROR_PATTERNS.CONNECTION_LOST)) {
      datadogLogs.logger.error(
        DATADOG_LOG_EVENT.QLIK_CONNECTION_ERROR,
        logBody
      );

      toast.error(
        <ToastBody
          category="error"
          title="Connection to server lost"
          text="This is usually due to a prolonged time of inactivity or resuming your session after the computer has entered sleep mode. Reload the page to continue."
        />,
        {
          toastId: 'qlik-connection-error-toast',
          autoClose: false
        }
      );
    } else if (
      qlikModalBodyContent?.includes(QLIK_ERROR_PATTERNS.PARALLEL_SESSIONS)
    ) {
      datadogLogs.logger.error(
        DATADOG_LOG_EVENT.QLIK_PARALLEL_SESSION,
        logBody
      );
      toast.error(
        <ToastBody
          category="error"
          title="Parallel session error"
          text={
            'Sorry for the inconvenience. You have temporarily been locked out of the system due to too many sessions active in parallel. Please wait a few minutes and try again. If the problem persists, contact the support team.'
          }
        />,
        {
          toastId: 'qlik-parallel-session-error-toast',
          autoClose: false
        }
      );
    } else if (
      qlikModalBodyContent?.includes(QLIK_ERROR_PATTERNS.SESSION_EXPIRED)
    ) {
      datadogLogs.logger.warn(
        DATADOG_LOG_EVENT.QLIK_SESSION_EXPIRATION,
        logBody
      );

      toast.error(
        <ToastBody
          category="error"
          title="Session expired"
          text={'Your session has expired. Please log in again to continue.'}
        />,
        {
          toastId: 'qlik-session-expired-error-toast',
          autoClose: false
        }
      );
      history.push({
        pathname: '/logout'
      });
    } else if (qlikModalBodyContent) {
      datadogLogs.logger.error(qlikModalBodyContent, logBody);

      toast.error(
        <ToastBody
          category="error"
          title="Unexpected error"
          text={
            'There was an unexpected error. Please reload the page to continue. If the problem persists, contact the support team.'
          }
        />,
        {
          toastId: 'qlik-generic-error-toast',
          autoClose: false
        }
      );
    }
  };

  useMutationObserver(document.body, mutationCallback, {
    userId: user?._id,
    config: { attributes: true, childList: true, subtree: true }
  });
};

export default useQlikConnectionObserver;
